import React from 'react'
import { Button, Card, CardContent, CardHeader, Input, Label, Switch } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { listRewardsExchangeSettings, updateRewardsExchangeSetting, type RewardsExchangeSetting } from '../../services/rewardsSettingsService'

function bpsToPercent(bps: number): number {
  return Number(bps || 0) / 100
}

function percentToBps(percent: number): number {
  const p = Number(percent || 0)
  if (!Number.isFinite(p)) return 0
  return Math.round(p * 100)
}

export default function RewardsSettingsExchangesCard() {
  const { add: toast } = useToast()
  const [loading, setLoading] = React.useState(true)
  const [savingKey, setSavingKey] = React.useState<string | null>(null)
  const [rows, setRows] = React.useState<RewardsExchangeSetting[]>([])
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    setError(null)
    setLoading(true)
    try {
      setRows(await listRewardsExchangeSettings())
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar corretoras')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  async function save(exchange: RewardsExchangeSetting['exchange']) {
    const row = rows.find((r) => r.exchange === exchange)
    if (!row) return
    setSavingKey(exchange)
    try {
      await updateRewardsExchangeSetting(exchange, {
        enabled: row.enabled,
        commission_bps: percentToBps(bpsToPercent(row.commission_bps)),
        maker_fee_rate: Number(row.maker_fee_rate || 0),
        taker_fee_rate: Number(row.taker_fee_rate || 0),
        signup_url: (row.signup_url || '').trim() || null,
        notes: (row.notes || '').trim() || null,
      })
      toast({ title: 'Rewards - Corretoras', description: `${exchange.toUpperCase()} salva.`, variant: 'success', soft: true })
      await load()
    } catch (e: any) {
      toast({ title: 'Rewards - Corretoras', description: e?.message || 'Falha ao salvar', variant: 'error' })
    } finally {
      setSavingKey(null)
    }
  }

  function setRow(exchange: RewardsExchangeSetting['exchange'], patch: Partial<RewardsExchangeSetting>) {
    setRows((prev) => prev.map((r) => (r.exchange === exchange ? { ...r, ...patch } : r)))
  }

  return (
    <Card>
      <CardHeader className="text-base font-semibold">Rewards - Corretoras (configurável)</CardHeader>
      <CardContent className="space-y-4">
        {error && <div className="rounded-md border border-red-500/30 bg-red-500/10 p-2 text-sm text-foreground">{error}</div>}
        {loading ? (
          <div className="text-sm text-foreground/70">Carregando...</div>
        ) : (
          <div className="space-y-4">
            {rows.map((r) => (
              <div key={r.exchange} className="rounded-md border border-border/60 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold">{r.exchange.toUpperCase()}</div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Ativo</Label>
                    <Switch checked={r.enabled} disabled={!!savingKey} onChange={(e) => setRow(r.exchange, { enabled: (e.target as HTMLInputElement).checked })} />
                  </div>
                </div>

                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label>Comissão (%)</Label>
                    <Input
                      type="number"
                      value={bpsToPercent(r.commission_bps)}
                      onChange={(e) => setRow(r.exchange, { commission_bps: percentToBps(Number((e.target as HTMLInputElement).value)) })}
                      disabled={!!savingKey}
                    />
                    <div className="text-xs text-foreground/60">Versionado no histórico (rewards_exchange_commission_history).</div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label>Maker fee (rate)</Label>
                      <Input
                        type="number"
                        value={r.maker_fee_rate}
                        onChange={(e) => setRow(r.exchange, { maker_fee_rate: Number((e.target as HTMLInputElement).value) })}
                        disabled={!!savingKey}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Taker fee (rate)</Label>
                      <Input
                        type="number"
                        value={r.taker_fee_rate}
                        onChange={(e) => setRow(r.exchange, { taker_fee_rate: Number((e.target as HTMLInputElement).value) })}
                        disabled={!!savingKey}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Link de cadastro (opcional)</Label>
                    <Input
                      value={r.signup_url || ''}
                      onChange={(e) => setRow(r.exchange, { signup_url: (e.target as HTMLInputElement).value })}
                      placeholder="https://..."
                      disabled={!!savingKey}
                    />
                  </div>

                  <div className="space-y-1.5 md:col-span-2">
                    <Label>Notas (opcional)</Label>
                    <Input
                      value={r.notes || ''}
                      onChange={(e) => setRow(r.exchange, { notes: (e.target as HTMLInputElement).value })}
                      placeholder="Observações internas..."
                      disabled={!!savingKey}
                    />
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <Button variant="outline" onClick={() => save(r.exchange)} disabled={!!savingKey}>
                    {savingKey === r.exchange ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

